import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

type MerchItem = {
  id: string;
  category: string;
  item_name: string;
  is_kid: boolean | null;
  price: number | null;
  item_group?: string | null;
  group_precedence?: number | null;
  archived: boolean;
};

type ItemGroup = "APPAREL" | "SPARRING" | "WEAPON";

type MerchSize = {
  id: string;
  size_name: string;
  // Flags may come back from Supabase as booleans, strings, or numbers depending on schema
  is_kid: boolean | string | number | null;
  is_shoe?: boolean | string | number | null;
  precedence?: number | null;
};

type MerchItemSize = {
  id: string;
  item_id: string;
  size_id: string;
  archived: boolean;
};

type MerchItemImage = {
  id: string;
  item_id: string;
  om_id: string;
  archived: boolean;
};

type MediaRow = Tables<"object_media">;

type ItemCategory = "ADULT" | "CHILD" | "SHOE";

const CATEGORY_LABELS: Record<ItemCategory, string> = {
  ADULT: "Adult",
  CHILD: "Child",
  SHOE: "Footwear",
};

export default function AdminMerchandise() {
  const { user } = useAuth();
  const [items, setItems] = useState<MerchItem[]>([]);
  const [sizes, setSizes] = useState<MerchSize[]>([]);
  const [itemSizes, setItemSizes] = useState<MerchItemSize[]>([]);
  const [itemImages, setItemImages] = useState<MerchItemImage[]>([]);
  const [merchMedia, setMerchMedia] = useState<MediaRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newItemName, setNewItemName] = useState("");
  const [newItemCategory, setNewItemCategory] = useState<ItemCategory>("ADULT");
  const [newItemGroup, setNewItemGroup] = useState<ItemGroup>("APPAREL");
  const [newItemPrice, setNewItemPrice] = useState<string>("");
  const [newItemSelectedSizeIds, setNewItemSelectedSizeIds] = useState<Set<string>>(new Set());
  const [isSavingNewItem, setIsSavingNewItem] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [savingItemId, setSavingItemId] = useState<string | null>(null);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [imageDialogItemId, setImageDialogItemId] = useState<string | null>(null);
  const [selectedImageIdsForDialog, setSelectedImageIdsForDialog] = useState<
    Set<string>
  >(new Set());
  const [isSavingImages, setIsSavingImages] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchMerchandiseMedia = async () => {
    try {
      // Find the "Merchandise" album (by album_string or album_name)
      const { data: albumData, error: albumError } = await supabase
        .from("object_media_album")
        .select("id,album_name,album_string")
        .or(
          "album_string.eq.merchandise,album_name.ilike.Merch%,album_name.ilike.March%",
        );

      if (albumError) {
        console.error("Error loading Merchandise album:", albumError);
        setMerchMedia([]);
        return;
      }

      const album = (albumData as any[] | null)?.[0];
      if (!album) {
        setMerchMedia([]);
        return;
      }

      const merchAlbumId = album.id as string;

      const { data: taggedData, error: taggedError } = await (supabase as any)
        .from("object_media_tagged")
        .select("om_id")
        .eq("oma_id", merchAlbumId)
        .eq("archived", false);

      if (taggedError) {
        console.error("Error loading Merchandise-tagged media:", taggedError);
        setMerchMedia([]);
        return;
      }

      const omIds = Array.from(
        new Set(
          ((taggedData as any[] | null) ?? []).map((row) => row.om_id as string),
        ),
      );

      if (omIds.length === 0) {
        setMerchMedia([]);
        return;
      }

      const { data: mediaData, error: mediaError } = await supabase
        .from("object_media")
        .select("*")
        .in("id", omIds)
        .eq("archived", false)
        .eq("is_image", true);

      if (mediaError) {
        console.error("Error loading Merchandise media rows:", mediaError);
        setMerchMedia([]);
        return;
      }

      setMerchMedia((mediaData as any[] as MediaRow[]) ?? []);
    } catch (error) {
      console.error("Unexpected error loading Merchandise media:", error);
      setMerchMedia([]);
    }
  };

  const fetchAll = async () => {
    try {
      setIsLoading(true);

      const [itemsRes, sizesRes, itemSizesRes, itemImagesRes] = await Promise.all([
        supabase.from("merch_cat_item").select("*"),
        supabase
          .from("merch_cat_size")
          .select("id,size_name,is_kid,is_shoe,precedence"),
        supabase.from("merch_cat_item_size").select("*").eq("archived", false),
        supabase.from("merch_cat_item_image").select("*").eq("archived", false),
      ]);

      if (itemsRes.error) throw itemsRes.error;
      if (sizesRes.error) throw sizesRes.error;
      if (itemSizesRes.error) throw itemSizesRes.error;
      if (itemImagesRes.error) throw itemImagesRes.error;

      setItems((itemsRes.data as any[] as MerchItem[]) ?? []);
      setSizes((sizesRes.data as any[] as MerchSize[]) ?? []);
      setItemSizes((itemSizesRes.data as any[] as MerchItemSize[]) ?? []);
      setItemImages((itemImagesRes.data as any[] as MerchItemImage[]) ?? []);

      // Fetch all media images tagged under the "Merchandise" album
      await fetchMerchandiseMedia();
    } catch (error) {
      console.error("Error loading merchandise data:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem loading merchandise items.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredItems = useMemo(
    () =>
      items.filter((item) => {
        if (!showArchived && item.archived) return false;
        return true;
      }),
    [items, showArchived],
  );

  type DisplayRow =
    | { type: "group"; groupName: string | null }
    | { type: "item"; item: MerchItem };

  const displayRows: DisplayRow[] = useMemo(() => {
    const groups = new Map<string | null, MerchItem[]>();

    filteredItems.forEach((item) => {
      const key = item.item_group ?? null;
      const groupItems = groups.get(key) ?? [];
      groupItems.push(item);
      groups.set(key, groupItems);
    });

    const groupEntries = Array.from(groups.entries()).sort(([a], [b]) => {
      const nameA = a ?? "zzz";
      const nameB = b ?? "zzz";
      return nameA.localeCompare(nameB);
    });

    const rows: DisplayRow[] = [];

    groupEntries.forEach(([groupName, groupItems]) => {
      rows.push({ type: "group", groupName });

      const sortedItems = [...groupItems].sort((a, b) => {
        const ap = a.group_precedence ?? 9999;
        const bp = b.group_precedence ?? 9999;
        if (ap !== bp) return ap - bp;
        return a.item_name.localeCompare(b.item_name);
      });

      sortedItems.forEach((item) => {
        rows.push({ type: "item", item });
      });
    });

    return rows;
  }, [filteredItems]);

  const hasArchivedItems = useMemo(
    () => items.some((item) => item.archived),
    [items],
  );

  const isFlagTrue = (value: any) =>
    value === true ||
    value === "true" ||
    value === 1 ||
    value === "1";

  const isFlagFalse = (value: any) =>
    value === false ||
    value === "false" ||
    value === 0 ||
    value === "0" ||
    value === null ||
    typeof value === "undefined";

  const sortSizes = (list: MerchSize[]): MerchSize[] => {
    return [...list].sort((a, b) => {
      const ap = a.precedence ?? 9999;
      const bp = b.precedence ?? 9999;
      if (ap !== bp) return ap - bp;
      return a.size_name.localeCompare(b.size_name);
    });
  };

  // Group sizes once into Adult / Child / Footwear exactly as requested:
  // - Adult:    is_kid = FALSE, is_shoe = FALSE
  // - Child:    is_kid = TRUE,  is_shoe = FALSE
  // - Footwear: is_shoe = TRUE
  const groupedSizes = useMemo(() => {
    const adult: MerchSize[] = [];
    const child: MerchSize[] = [];
    const footwear: MerchSize[] = [];

    sizes.forEach((s) => {
      const kid = isFlagTrue(s.is_kid);
      const shoe = isFlagTrue(s.is_shoe);

      if (!kid && !shoe) {
        adult.push(s);
      } else if (kid && !shoe) {
        child.push(s);
      } else if (shoe) {
        footwear.push(s);
      }
    });

    return {
      ADULT: sortSizes(adult),
      CHILD: sortSizes(child),
      SHOE: sortSizes(footwear),
    };
  }, [sizes]);

  const getSizesForCategory = (category: ItemCategory): MerchSize[] =>
    groupedSizes[category] ?? [];

  const toggleNewItemSize = (sizeId: string) => {
    setNewItemSelectedSizeIds((prev) => {
      const next = new Set(prev);
      if (next.has(sizeId)) {
        next.delete(sizeId);
      } else {
        next.add(sizeId);
      }
      return next;
    });
  };

  const resetNewItemForm = () => {
    setNewItemName("");
    setNewItemCategory("ADULT");
    setNewItemGroup("APPAREL");
    setNewItemPrice("");
    setNewItemSelectedSizeIds(new Set());
  };

  const handleCreateItem = async () => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Not signed in",
        description: "You must be signed in to manage merchandise.",
      });
      return;
    }

    const trimmedName = newItemName.trim();
    if (!trimmedName) {
      toast({
        variant: "destructive",
        title: "Item name required",
        description: "Please enter an item name.",
      });
      return;
    }

    const parsedPrice = newItemPrice.trim()
      ? parseFloat(newItemPrice.trim())
      : 0;
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      toast({
        variant: "destructive",
        title: "Invalid price",
        description: "Please enter a valid price.",
      });
      return;
    }

    if (newItemSelectedSizeIds.size === 0) {
      toast({
        variant: "destructive",
        title: "No sizes selected",
        description: "Please select at least one size for this item.",
      });
      return;
    }

    setIsSavingNewItem(true);

    try {
      const isKid = newItemCategory === "CHILD";

      const { data, error } = await supabase
        .from("merch_cat_item")
        .insert({
          item_name: trimmedName,
          category: newItemCategory,
          is_kid: isKid,
          price: parsedPrice,
          item_group: newItemGroup,
          created_by: user.id,
        })
        .select("*")
        .single();

      if (error) {
        throw error;
      }

      const createdItem = data as any as MerchItem;

      // Insert size relations
      const sizeIds = Array.from(newItemSelectedSizeIds);
      if (sizeIds.length > 0) {
        const { error: sizeError } = await supabase
          .from("merch_cat_item_size")
          .insert(
            sizeIds.map((sizeId) => ({
              item_id: createdItem.id,
              size_id: sizeId,
              created_by: user.id,
            })),
          );

        if (sizeError) {
          throw sizeError;
        }
      }

      toast({
        title: "Item created",
        description: "The merchandise item has been created.",
      });

      resetNewItemForm();
      setIsDialogOpen(false);
      await fetchAll();
    } catch (error) {
      console.error("Error creating merch item:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem creating the merchandise item.",
      });
    } finally {
      setIsSavingNewItem(false);
    }
  };

  const handleUpdateItemField = async (
    item: MerchItem,
    field: keyof MerchItem,
    value: any,
  ) => {
    if (!user) return;

    setSavingItemId(item.id);
    try {
      const { error } = await supabase
        .from("merch_cat_item")
        .update({ [field]: value })
        .eq("id", item.id);

      if (error) throw error;

      setItems((prev) =>
        prev.map((it) =>
          it.id === item.id ? { ...it, [field]: value } : it,
        ),
      );
    } catch (error) {
      console.error("Error updating merch item:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem saving your changes.",
      });
    } finally {
      setSavingItemId(null);
    }
  };

  const handleToggleItemSize = async (
    itemId: string,
    sizeId: string,
    checked: boolean,
  ) => {
    if (!user) return;

    try {
      // Find existing link (even if archived)
      const existing = itemSizes.find(
        (rel) => rel.item_id === itemId && rel.size_id === sizeId,
      );

      if (checked) {
        if (existing) {
          // Unarchive if needed
          if (existing.archived) {
            const { error } = await supabase
              .from("merch_cat_item_size")
              .update({
                archived: false,
                archived_by: null,
                archive_date: null,
              })
              .eq("id", existing.id);
            if (error) throw error;
          }
        } else {
          const { error } = await supabase
            .from("merch_cat_item_size")
            .insert({
              item_id: itemId,
              size_id: sizeId,
              created_by: user.id,
            });
          if (error) throw error;
        }
      } else if (existing) {
        // Archive relation
        const { error } = await supabase
          .from("merch_cat_item_size")
          .update({
            archived: true,
            archived_by: user.id,
            archive_date: new Date().toISOString(),
          })
          .eq("id", existing.id);
        if (error) throw error;
      }

      await fetchAll();
    } catch (error) {
      console.error("Error updating item sizes:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem updating sizes for this item.",
      });
    }
  };

  const handleToggleArchived = async (item: MerchItem) => {
    if (!user) return;

    const newArchived = !item.archived;

    try {
      const { error } = await supabase
        .from("merch_cat_item")
        .update({
          archived: newArchived,
          archived_by: newArchived ? user.id : null,
          archive_date: newArchived ? new Date().toISOString() : null,
        })
        .eq("id", item.id);

      if (error) throw error;

      setItems((prev) =>
        prev.map((it) =>
          it.id === item.id
            ? {
                ...it,
                archived: newArchived,
              }
            : it,
        ),
      );
    } catch (error) {
      console.error("Error toggling item archived state:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem updating the item state.",
      });
    }
  };

  const getActiveSizeIdsForItem = (itemId: string): Set<string> => {
    const set = new Set<string>();
    itemSizes.forEach((rel) => {
      if (rel.item_id === itemId && !rel.archived) {
        set.add(rel.size_id);
      }
    });
    return set;
  };

  const getImagesForItem = (itemId: string): MediaRow[] => {
    if (!itemImages.length || !merchMedia.length) return [];
    const mediaById = new Map(merchMedia.map((m) => [m.id, m]));
    return itemImages
      .filter((rel) => rel.item_id === itemId && !rel.archived)
      .map((rel) => mediaById.get(rel.om_id))
      .filter((m): m is MediaRow => Boolean(m));
  };

  const openImageDialogForItem = (item: MerchItem) => {
    const images = getImagesForItem(item.id);
    setImageDialogItemId(item.id);
    setSelectedImageIdsForDialog(new Set(images.map((m) => m.id)));
    setIsImageDialogOpen(true);
  };

  const toggleDialogImageSelection = (mediaId: string) => {
    setSelectedImageIdsForDialog((prev) => {
      const next = new Set(prev);
      if (next.has(mediaId)) {
        next.delete(mediaId);
      } else {
        next.add(mediaId);
      }
      return next;
    });
  };

  const handleSaveItemImages = async () => {
    if (!user || !imageDialogItemId) return;
    setIsSavingImages(true);

    try {
      // Load all existing relations for this item (including archived)
      const { data: existingData, error } = await supabase
        .from("merch_cat_item_image")
        .select("*")
        .eq("item_id", imageDialogItemId);

      if (error) throw error;

      const existing = (existingData as any[] as MerchItemImage[]) ?? [];
      const selectedIds = Array.from(selectedImageIdsForDialog);

      // Upsert / unarchive selected
      for (const omId of selectedIds) {
        const existingRel = existing.find((rel) => rel.om_id === omId);
        if (existingRel) {
          if (existingRel.archived) {
            const { error: unarchiveError } = await supabase
              .from("merch_cat_item_image")
              .update({
                archived: false,
                archived_by: null,
                archive_date: null,
              })
              .eq("id", existingRel.id);
            if (unarchiveError) throw unarchiveError;
          }
        } else {
          const { error: insertError } = await supabase
            .from("merch_cat_item_image")
            .insert({
              item_id: imageDialogItemId,
              om_id: omId,
            });
          if (insertError) throw insertError;
        }
      }

      // Archive any existing active relations that are no longer selected
      const toArchive = existing.filter(
        (rel) => !rel.archived && !selectedIds.includes(rel.om_id),
      );
      if (toArchive.length > 0) {
        const idsToArchive = toArchive.map((rel) => rel.id);
        const { error: archiveError } = await supabase
          .from("merch_cat_item_image")
          .update({
            archived: true,
            archived_by: user.id,
            archive_date: new Date().toISOString(),
          })
          .in("id", idsToArchive);
        if (archiveError) throw archiveError;
      }

      toast({
        title: "Images updated",
        description: "Merchandise images for this item have been updated.",
      });

      setIsImageDialogOpen(false);
      setImageDialogItemId(null);
      setSelectedImageIdsForDialog(new Set());
      await fetchAll();
    } catch (error) {
      console.error("Error updating merch item images:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was a problem updating images for this item.",
      });
    } finally {
      setIsSavingImages(false);
    }
  };

  const renderItemRow = (item: MerchItem) => {
    const category = (item.category?.toUpperCase() as ItemCategory) || "ADULT";
    const availableSizes = getSizesForCategory(category);
    const activeSizeIds = getActiveSizeIdsForItem(item.id);
    const imagesForItem = getImagesForItem(item.id);

    return (
      <TableRow
        key={item.id}
        className={item.archived ? "bg-gray-100 text-gray-500" : ""}
      >
        <TableCell className="align-top min-w-[300px]">
          <Input
            value={item.item_name}
            onChange={(e) =>
              setItems((prev) =>
                prev.map((it) =>
                  it.id === item.id ? { ...it, item_name: e.target.value } : it,
                ),
              )
            }
            onBlur={(e) =>
              handleUpdateItemField(item, "item_name", e.target.value.trim())
            }
            disabled={item.archived}
          />
          {imagesForItem.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {imagesForItem.map((media) => {
                const rawName = media.name ?? media.url.split("/").pop() ?? "";
                const baseName = rawName.includes(".")
                  ? rawName.slice(0, rawName.lastIndexOf("."))
                  : rawName;
                const displayName = baseName.replace(/_/g, " ");

                return (
                  <Dialog key={media.id}>
                    <DialogTrigger asChild>
                      <button
                        type="button"
                        className="h-10 w-10 overflow-hidden rounded border bg-slate-50"
                      >
                        <img
                          src={media.url}
                          alt={displayName || "Merch item image"}
                          className="h-full w-full object-cover"
                        />
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl">
                      <DialogHeader>
                        <DialogTitle className="text-sm">
                          {displayName || "Merch item image"}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="mt-2">
                        <img
                          src={media.url}
                          alt={displayName || "Merch item image"}
                          className="w-full max-h-[70vh] object-contain rounded bg-slate-100"
                        />
                      </div>
                    </DialogContent>
                  </Dialog>
                );
              })}
            </div>
          )}
        </TableCell>
        <TableCell className="align-top min-w-[200px]">
          <div className="space-y-2">
            <Select
              value={category}
              onValueChange={(val: ItemCategory) =>
                handleUpdateItemField(item, "category", val)
              }
              disabled={item.archived}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ADULT">{CATEGORY_LABELS.ADULT}</SelectItem>
                <SelectItem value="CHILD">{CATEGORY_LABELS.CHILD}</SelectItem>
                <SelectItem value="SHOE">{CATEGORY_LABELS.SHOE}</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex flex-wrap gap-2">
              {availableSizes.map((size) => {
                const checked = activeSizeIds.has(size.id);
                return (
                  <label
                    key={size.id}
                    className="inline-flex items-center gap-1 text-xs cursor-pointer"
                  >
                    <Checkbox
                      checked={checked}
                      disabled={item.archived}
                      onCheckedChange={(val) =>
                        handleToggleItemSize(item.id, size.id, Boolean(val))
                      }
                    />
                    <span>{size.size_name}</span>
                  </label>
                );
              })}
              {availableSizes.length === 0 && (
                <span className="text-xs text-muted-foreground">
                  No sizes configured for this category.
                </span>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell className="align-top min-w-[140px]">
          <Input
            type="number"
            step="0.01"
            value={item.price ?? 0}
            onChange={(e) =>
              setItems((prev) =>
                prev.map((it) =>
                  it.id === item.id
                    ? { ...it, price: parseFloat(e.target.value) || 0 }
                    : it,
                ),
              )
            }
            onBlur={(e) =>
              handleUpdateItemField(
                item,
                "price",
                parseFloat(e.target.value) || 0,
              )
            }
            disabled={item.archived}
          />
        </TableCell>
        <TableCell className="align-top min-w-[200px]">
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={item.archived}
              onClick={() => openImageDialogForItem(item)}
            >
              Add Images
            </Button>
            <Button
              variant={item.archived ? "outline" : "destructive"}
              size="sm"
              onClick={() => handleToggleArchived(item)}
            >
              {item.archived ? "Relist" : "Delist"}
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
  };

  const categorySizesForDialog = getSizesForCategory(newItemCategory);

  return (
    <div className="w-full px-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-2xl font-semibold text-taekwondo-red mb-6 mt-4">Merchandise</h2>
          <p className="text-sm text-muted-foreground">
            Manage catalogue items, sizes, and availability.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {hasArchivedItems && (
            <Button
              variant={showArchived ? "default" : "outline"}
              size="sm"
              onClick={() => setShowArchived((prev) => !prev)}
            >
              {showArchived ? "Hide Delisted" : "Show Delisted"}
            </Button>
          )}
          <Button
            size="sm"
            className="bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white"
            onClick={() => {
              resetNewItemForm();
              setIsDialogOpen(true);
            }}
          >
            Add Item
          </Button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading merchandise...</p>
      ) : filteredItems.length === 0 ? (
        <div className="bg-gray-50 rounded p-6 text-center">
          <p className="text-gray-500">
            No merchandise items found. Click &quot;Add Item&quot; to create one.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-gray-100 text-taekwondo-red min-w-[300px]">
                  Item Name
                </TableHead>
                <TableHead className="bg-gray-100 text-taekwondo-red min-w-[200px]">
                  Category / Sizes
                </TableHead>
                <TableHead className="bg-gray-100 text-taekwondo-red min-w-[140px]">
                  Price
                </TableHead>
                <TableHead className="bg-gray-100 text-taekwondo-red min-w-[200px]">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayRows.map((row, index) =>
                row.type === "group" ? (
                  <TableRow key={`group-${row.groupName ?? "ungrouped"}-${index}`}>
                    <TableCell
                      colSpan={4}
                      className="bg-slate-50 font-semibold text-taekwondo-red"
                    >
                      {row.groupName || "Other Merchandise"}
                    </TableCell>
                  </TableRow>
                ) : (
                  renderItemRow(row.item)
                ),
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Catalogue Item</DialogTitle>
            <DialogDescription className="sr-only">
              Create a new merchandise catalogue item with category, price, and available sizes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Item name</label>
              <Input
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                placeholder="e.g. OMAC Hoodie"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Item group</label>
              <Select
                value={newItemGroup}
                onValueChange={(val: ItemGroup) => setNewItemGroup(val)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="APPAREL">Apparel</SelectItem>
                  <SelectItem value="SPARRING">Sparring</SelectItem>
                  <SelectItem value="WEAPON">Weapon</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Category</label>
              <Select
                value={newItemCategory}
                onValueChange={(val: ItemCategory) => {
                  setNewItemCategory(val);
                  setNewItemSelectedSizeIds(new Set());
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADULT">{CATEGORY_LABELS.ADULT}</SelectItem>
                  <SelectItem value="CHILD">{CATEGORY_LABELS.CHILD}</SelectItem>
                  <SelectItem value="SHOE">{CATEGORY_LABELS.SHOE}</SelectItem>
                </SelectContent>
              </Select>
              <div className="pt-1 space-y-1">
                <span className="text-xs font-medium text-muted-foreground">
                  Available sizes
                </span>
                <div className="flex flex-wrap gap-2">
                  {categorySizesForDialog.map((size) => (
                    <label
                      key={size.id}
                      className="inline-flex items-center gap-1 text-xs cursor-pointer"
                    >
                      <Checkbox
                        checked={newItemSelectedSizeIds.has(size.id)}
                        onCheckedChange={() => toggleNewItemSize(size.id)}
                      />
                      <span>{size.size_name}</span>
                    </label>
                  ))}
                  {categorySizesForDialog.length === 0 && (
                    <span className="text-xs text-muted-foreground">
                      No sizes configured for this category yet.
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Price</label>
              <Input
                type="number"
                step="0.01"
                value={newItemPrice}
                onChange={(e) => setNewItemPrice(e.target.value)}
                placeholder="e.g. 29.99"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
              }}
              disabled={isSavingNewItem}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateItem}
              disabled={isSavingNewItem}
              className="bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white"
            >
              {isSavingNewItem ? "Saving..." : "Done"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isImageDialogOpen}
        onOpenChange={(open) => {
          setIsImageDialogOpen(open);
          if (!open) {
            setImageDialogItemId(null);
            setSelectedImageIdsForDialog(new Set());
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Select Images</DialogTitle>
            <DialogDescription>
              Choose one or more Merchandise images to associate with this item.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {merchMedia.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No images are currently tagged to the &quot;Merchandise&quot; album. Tag
                images in the Media Library first to make them available here.
              </p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 max-h-[420px] overflow-y-auto pr-1">
                {merchMedia.map((media) => {
                  const selected = selectedImageIdsForDialog.has(media.id);
                  const rawName =
                    media.name ?? media.url.split("/").pop() ?? "Image";
                  const baseName = rawName.includes(".")
                    ? rawName.slice(0, rawName.lastIndexOf("."))
                    : rawName;
                  return (
                    <button
                      key={media.id}
                      type="button"
                      onClick={() => toggleDialogImageSelection(media.id)}
                      className={[
                        "relative rounded border bg-slate-50 w-full overflow-hidden",
                        selected
                          ? "ring-2 ring-taekwondo-blue border-taekwondo-blue"
                          : "hover:border-slate-400",
                      ].join(" ")}
                    >
                      <div className="h-20 w-full overflow-hidden">
                        <img
                          src={media.url}
                          alt={media.name ?? "Merchandise image"}
                          className="h-full w-full object-cover"
                        />
                      </div>
                      <div className="px-1 py-0.5">
                        <p className="text-[10px] leading-tight text-left line-clamp-2">
                          {baseName}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsImageDialogOpen(false);
              }}
              disabled={isSavingImages}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveItemImages}
              disabled={isSavingImages || !imageDialogItemId}
              className="bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white"
            >
              {isSavingImages ? "Saving..." : "Save Images"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


